import {api} from '../../../shared/api';
import {AdminConversationsParams, AuthorKind, ConversationListItem, ConversationSeenState, Message, MessagePage, RelayPaging} from '../model';

const REP_BASE = '/api/fundraising/representative/conversations';
const ADMIN_BASE = '/api/fundraising/admin/conversations';

interface MessageDto {
  id: string;
  authorKind: string;
  authorId?: string | null;
  text: string;
  createdAt: string;
}

interface PageInfoDto {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor?: string | null;
  endCursor?: string | null;
}

interface MessagePageDto {
  items: MessageDto[];
  pageInfo: PageInfoDto;
  totalCount: number;
}

interface ConversationListItemDto {
  conversationId: string;
  appealId: string;
  appealName: string;
  fundName: string;
  lastMessageAt: string;
  lastMessageAuthorKind: string;
  lastMessageText: string;
  awaitingReply: boolean;
  hasUnreadMessages?: boolean;
}

interface ConversationSeenStateDto {
  seenAt?: string | null;
  lastMessageAt?: string | null;
  hasUnreadMessages?: boolean;
}

interface ConversationListPageDto {
  items: ConversationListItemDto[];
  pageInfo: PageInfoDto;
  totalCount: number;
}

function mapMessage(dto: MessageDto): Message {
  return {
    id: dto.id,
    authorKind: dto.authorKind as AuthorKind,
    authorId: dto.authorId ?? null,
    text: dto.text,
    createdAt: dto.createdAt,
  };
}

function mapConversationListItem(dto: ConversationListItemDto): ConversationListItem {
  return {
    conversationId: dto.conversationId,
    appealId: dto.appealId,
    appealName: dto.appealName,
    fundName: dto.fundName,
    lastMessageAt: dto.lastMessageAt,
    lastMessageAuthorKind: dto.lastMessageAuthorKind as AuthorKind,
    lastMessageText: dto.lastMessageText,
    awaitingReply: dto.awaitingReply,
    hasUnreadMessages: dto.hasUnreadMessages ?? false,
  };
}

function mapSeenState(dto: ConversationSeenStateDto): ConversationSeenState {
  return {
    seenAt: dto.seenAt ?? null,
    lastMessageAt: dto.lastMessageAt ?? null,
    hasUnreadMessages: dto.hasUnreadMessages ?? false,
  };
}

function buildPagingParams(paging: RelayPaging): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (paging.First !== undefined) params.First = paging.First;
  if (paging.After !== undefined) params.After = paging.After;
  if (paging.Last !== undefined) params.Last = paging.Last;
  if (paging.Before !== undefined) params.Before = paging.Before;
  if (paging.IncludeTotalCount !== undefined) params.IncludeTotalCount = paging.IncludeTotalCount;
  return params;
}

export const conversationApi = {
  getRepMessages: async (appealId: string, paging: RelayPaging = {}): Promise<MessagePage> => {
    const {data} = await api.get<MessagePageDto>(`${REP_BASE}/${appealId}/messages`, {params: buildPagingParams(paging)});
    return {
      items: data.items.map(mapMessage),
      pageInfo: {
        hasNextPage: data.pageInfo.hasNextPage,
        hasPreviousPage: data.pageInfo.hasPreviousPage,
        startCursor: data.pageInfo.startCursor ?? null,
        endCursor: data.pageInfo.endCursor ?? null,
      },
      totalCount: data.totalCount,
    };
  },

  sendRepMessage: async (appealId: string, text: string): Promise<Message> => {
    const {data} = await api.post<MessageDto>(`${REP_BASE}/${appealId}/messages`, {text});
    return mapMessage(data);
  },

  getAdminMessages: async (appealId: string, paging: RelayPaging = {}): Promise<MessagePage> => {
    const {data} = await api.get<MessagePageDto>(`${ADMIN_BASE}/${appealId}/messages`, {params: buildPagingParams(paging)});
    return {
      items: data.items.map(mapMessage),
      pageInfo: {
        hasNextPage: data.pageInfo.hasNextPage,
        hasPreviousPage: data.pageInfo.hasPreviousPage,
        startCursor: data.pageInfo.startCursor ?? null,
        endCursor: data.pageInfo.endCursor ?? null,
      },
      totalCount: data.totalCount,
    };
  },

  sendAdminMessage: async (appealId: string, text: string): Promise<Message> => {
    const {data} = await api.post<MessageDto>(`${ADMIN_BASE}/${appealId}/messages`, {text});
    return mapMessage(data);
  },

  getAdminConversations: async (params: AdminConversationsParams = {}): Promise<{items: ConversationListItem[]; pageInfo: PageInfoDto; totalCount: number}> => {
    const {Search, FundId, AppealId, ...paging} = params;
    const queryParams: Record<string, unknown> = {...buildPagingParams(paging)};
    if (Search !== undefined) queryParams.Search = Search;
    if (FundId !== undefined) queryParams.FundId = FundId;
    if (AppealId !== undefined) queryParams.AppealId = AppealId;

    const {data} = await api.get<ConversationListPageDto>(ADMIN_BASE, {params: queryParams});
    return {
      items: data.items.map(mapConversationListItem),
      pageInfo: {
        hasNextPage: data.pageInfo.hasNextPage,
        hasPreviousPage: data.pageInfo.hasPreviousPage,
        startCursor: data.pageInfo.startCursor ?? null,
        endCursor: data.pageInfo.endCursor ?? null,
      },
      totalCount: data.totalCount,
    };
  },

  getRepSeenState: async (appealId: string): Promise<ConversationSeenState> => {
    const {data} = await api.get<ConversationSeenStateDto>(`${REP_BASE}/${appealId}/seen`);
    return mapSeenState(data);
  },

  markRepSeen: async (appealId: string, seenUpTo: string): Promise<void> => {
    await api.post(`${REP_BASE}/${appealId}/seen`, {seenUpTo});
  },

  getAdminSeenState: async (appealId: string): Promise<ConversationSeenState> => {
    const {data} = await api.get<ConversationSeenStateDto>(`${ADMIN_BASE}/${appealId}/seen`);
    return mapSeenState(data);
  },

  markAdminSeen: async (appealId: string, seenUpTo: string): Promise<void> => {
    await api.post(`${ADMIN_BASE}/${appealId}/seen`, {seenUpTo});
  },
};
